import type { components, paths } from '@connected/api-client/api.generated';
import type { Subtypes } from '@connected/ui/config/file';
import type { FileMetaData } from '@connected/ui/hooks/use-file';
import type { FetchOptions } from 'openapi-fetch';

import { api } from '@connected/api-client';
import { FileInput } from '@connected/ui/components/file';
import { DEFAULT_FILE_ORDER } from '@connected/ui/config/file';
import { useMemo } from 'react';
import { toast } from 'sonner';

const fileTypes = {
    // TODO: implement documentTemplateFile
    distributionboard: '/api/v2/electrical/projects/files/upload/distributionboard',
    finalcircuit: '/api/v2/electrical/projects/files/upload/finalcircuit',
    objectimage: '/api/v2/electrical/projects/files/upload/objectimage',
    observation: '/api/v2/electrical/projects/files/upload/observation',
    technicaldrawing: '/api/v2/electrical/projects/files/upload/technicaldrawing',
    organizationLogo: '/api/v2/organizations/files/logos/create',
    signature: '/api/v2/users/files/signatures/create',
} as const;

export type FileTypes = typeof fileTypes;
export type FileType = keyof FileTypes;

type FileUploadResult = components['schemas']['UploadFileCommandResult'];

export type FileFieldProps<TSelectedType extends FileType, TOptions extends FetchOptions<paths[FileTypes[TSelectedType]]['post']> = FetchOptions<paths[FileTypes[TSelectedType]]['post']>> = {
    name?: string;
    type: TSelectedType;
    options: Omit<TOptions, 'body' | 'bodySerializer'>;
    onSuccess: (result: FileUploadResult, metaData: FileMetaData) => void;
    disabled?: boolean;
};

/**
 * ### **Paths**:
 * - /api/v2/documents/templates/settings/files/upload
 * - /api/v2/electrical/projects/files/upload/**finalcircuit**
 * - /api/v2/electrical/projects/files/upload/**objectimage**
 * - /api/v2/electrical/projects/files/upload/**observation**
 * - /api/v2/electrical/projects/files/upload/**technicaldrawing**
 */
export function FileField<TSelectedType extends FileType>(props: FileFieldProps<TSelectedType>) {
    const { name, type, options: { params, ...options }, onSuccess, disabled = false } = props;

    const url: keyof paths = fileTypes[type];

    const query = api.useQuery('get', url, {});
    const mutation = api.useMutation('post', url, {
        onSuccess: (result, variables) => {
            const file = variables.body.File as unknown as File;

            onSuccess(result, {
                ContentType: file?.type,
                Name: file?.name,
                LastUpdateDate: file?.lastModified.toString(),
                Size: file?.size,
            });
        },
        onError: (error) => {
            toast.error(error?.Message ?? 'Failed to upload file');
        },
    });

    const isDisabled = useMemo<boolean>(
        () => query.isLoading || mutation.isPending || disabled,
        [query.isLoading, mutation.isPending, disabled],
    );

    async function handleChange(fileList: FileList) {
        if (fileList.length === 0)
            return;

        const promises = Array.from(fileList).map(async (file) => {
            const result = await mutation.mutateAsync({
                ...options,
                params: {
                    ...params,
                    query: {
                        Order: DEFAULT_FILE_ORDER,
                        ...params.query,
                        // ElectricalProjectFileId: crypto.randomUUID(),
                    },
                },
                // @ts-expect-error - File is not a string
                body: { File: file },
                bodySerializer: (body) => {
                    const formData = new FormData();
                    formData.append('File', body.File);
                    return formData;
                },
            });

            toast.success(`Bestand ${file.name} succesvol geüpload`);

            return result;
        });

        const results = await Promise.allSettled(promises);

        const failed = results.filter(
            (result): result is PromiseRejectedResult =>
                result.status === 'rejected',
        );

        if (failed.length > 0) {
            toast.error(
                `Fout bij uploaden van ${failed.length} van ${fileList.length} bestanden`,
            );
        }
    }

    return (
        <FileInput
            name={name}
            onChange={handleChange}
            loading={query.isLoading}
            disabled={isDisabled}
            subtype={query.data?.AllowedFileTypes as unknown as Subtypes}
            minFileSize={query.data?.MinFileSize}
            maxFileSize={query.data?.MaxFileSize}
            maxFileName={query.data?.MaxFileNameLength}
            mode={type === 'objectimage' || type === 'observation' ? 'both' : undefined}
        />
    );
}
